import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/use-toast';
import { Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccountFinal } from '@/api/entities';
import { Progress } from '@/components/ui/progress';

export default function DigitalFundingUploader() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setUploadResults(null);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const downloadTemplate = () => {
    const csv = 'account_id,digital_funding\nau_001_001,"Government digital transformation grant available for $500K|Federal COVID recovery funding prioritizing online learning infrastructure|State-level EdTech innovation fund with matching requirements"\nau_001_002,"Eligible for national research infrastructure grants|Regional development fund for technology adoption"';
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'digital_funding_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file to upload',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setUploadResults(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      const headers = lines[0].split(',').map(h => h.trim());
      
      if (headers[0] !== 'account_id' || headers[1] !== 'digital_funding') {
        throw new Error('CSV must have columns: account_id, digital_funding (in that order)');
      }

      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const allAccounts = await AccountFinal.list();
      const accountMap = new Map(allAccounts.map(acc => [acc.account_id || acc.id, acc]));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      toast({
        title: 'Processing...',
        description: `Updating ${lines.length - 1} accounts with digital funding data`,
        duration: 10000
      });

      for (let i = 1; i < lines.length; i++) {
        try {
          const values = parseCSVLine(lines[i]);
          const accountId = values[0]?.trim();
          const digitalFundingStr = values[1]?.trim();

          if (!accountId) {
            console.warn(`Row ${i + 1}: Missing account_id, skipping`);
            errorCount++;
            errors.push(`Row ${i + 1}: Missing account_id`);
            continue;
          }

          const account = accountMap.get(accountId);
          if (!account) {
            console.warn(`Row ${i + 1}: Account not found with ID: ${accountId}`);
            errorCount++;
            errors.push(`Row ${i + 1}: Account not found: ${accountId}`);
            continue;
          }

          // Parse digital_funding - split by pipe character
          const digitalFunding = digitalFundingStr 
            ? digitalFundingStr.split('|').map(item => item.trim()).filter(Boolean)
            : [];

          await AccountFinal.update(account.id, {
            digital_funding: digitalFunding
          });

          successCount++;
          console.log(`✅ Row ${i + 1}: Updated ${account.account_name}`);
          
          setProgress(Math.round((i / (lines.length - 1)) * 100));
        } catch (error) {
          console.error(`Row ${i + 1} error:`, error);
          errorCount++;
          errors.push(`Row ${i + 1}: ${error.message}`);
        }
      }

      setUploadResults({ successCount, errorCount, errors });
      
      toast({
        title: 'Upload complete!',
        description: `Successfully updated ${successCount} accounts. ${errorCount} errors.`,
        variant: errorCount > 0 ? 'default' : 'default'
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive'
      });
      setUploadResults({ successCount: 0, errorCount: 1, errors: [error.message] });
    } finally {
      setIsUploading(false);
      setProgress(100);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Digital Funding Opportunities Uploader
        </CardTitle>
        <CardDescription>
          Upload digital funding opportunities for accounts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>CSV Format:</strong>
            <br />
            • Column 1: <code className="bg-gray-100 px-1 rounded">account_id</code> (exact match required)
            <br />
            • Column 2: <code className="bg-gray-100 px-1 rounded">digital_funding</code> (multiple bullet points separated by pipe |)
            <br />
            <br />
            <strong>Example:</strong>
            <br />
            <code className="bg-gray-100 px-1 rounded text-xs">
              au_001_001,"Grant A available|Federal funding for EdTech|State innovation fund"
            </code>
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="csv-upload">Upload CSV File</Label>
          <Input
            id="csv-upload"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </div>

        {file && (
          <div className="p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-900">
              <strong>Selected file:</strong> {file.name}
            </p>
          </div>
        )}

        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Upload className="w-4 h-4 mr-2 animate-spin" />
              Uploading... {progress}%
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Digital Funding Data
            </>
          )}
        </Button>

        {isUploading && (
          <Progress value={progress} className="w-full" />
        )}

        {uploadResults && (
          <Alert variant={uploadResults.errorCount > 0 ? 'destructive' : 'default'}>
            {uploadResults.errorCount > 0 ? (
              <AlertCircle className="h-4 h-4" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              <strong>Upload Results:</strong>
              <br />
              ✅ Successfully updated: {uploadResults.successCount} accounts
              <br />
              ❌ Errors: {uploadResults.errorCount}
              {uploadResults.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm font-semibold">
                    View errors ({uploadResults.errors.length})
                  </summary>
                  <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                    {uploadResults.errors.map((error, idx) => (
                      <li key={idx} className="text-red-600">• {error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}